import React from 'react';
import useAliceGPT from '../../../../Hooks/AliceGPT/useAliceGPT';
import * as AliceGPTEvents from '../../../../Context/AliceGPT/AliceGPTContextEvents';
import startsWithNumber from '../../../Helpers/Validators/startsWithNumber';
import { useTranslation } from 'react-i18next';
import { LanguagesMap } from '../../../OrganizationSettings/LanguagesMap';
import './AliceProjectDetailsForm.modern.css'; // NEW import

function AliceProjectDetailsForm({ checkDuplicateProjectName, showAIFields, showLanguageDropdown }) {
    const { t } = useTranslation("create_project/alice_project_details_form", { useSuspense: false });
    const { t: workspaceT } = useTranslation("workspace_create_app_page", { useSuspense: false });

    const [{ form, showAdvancedConfigurations, selectedFile, fileName, fileError }, dispatch] = useAliceGPT();

    const isProjectNameUsed = checkDuplicateProjectName();
    const hasSpecialCharacters = /[^a-zA-Z0-9 ]/g.test(form.projectName);

    function handleFormChange(e) {
        const { name, value, maxLength } = e.target;
        let newValue = value;
        if (maxLength && maxLength > 0 && value.length > maxLength) {
            newValue = value.substring(0, maxLength);
        }
        const newFormDetails = { ...form, [name]: newValue };
        dispatch({ type: AliceGPTEvents.FORM_UPDATED, payload: newFormDetails });
    }

    function handleFileChange(e) {
        const file = e.target.files[0];
        if (file) {
            // Check file size
            if (file.size > 10 * 1024 * 1024) { // 10MB limit
                dispatch({ type: AliceGPTEvents.FILE_ERROR_UPDATED, payload: "File size exceeds 10MB limit." });
                dispatch({ type: AliceGPTEvents.FILE_CLEARED });
                return;
            }
            
            // Check file type
            const allowedTypes = ['.pdf', '.doc', '.docx', '.txt'];
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
            if (!allowedTypes.includes(fileExtension)) {
                dispatch({ type: AliceGPTEvents.FILE_ERROR_UPDATED, payload: "Only PDF, DOC, DOCX, and TXT files are allowed." });
                dispatch({ type: AliceGPTEvents.FILE_CLEARED });
                return;
            }
            
            dispatch({ type: AliceGPTEvents.FILE_ERROR_UPDATED, payload: "" });
            dispatch({ type: AliceGPTEvents.FILE_SELECTED, payload: file });
            dispatch({ type: AliceGPTEvents.FILE_NAME_UPDATED, payload: file.name });
        }
    }

    function handlePaste(e) {
        // Paste functionality removed since only document files are allowed
        // Images can no longer be pasted as they are not in the allowed file types
    }

    return (
        <div className="alice-form-container">
            <div className="alice-form-card">
                <h4 className="alice-form-title mb-2">{t("tell_us_about_your_app")}</h4>
                <p className="alice-form-subtitle mb-2">{t("help_us_understand")}</p>
                <form onSubmit={e => e.preventDefault()}>
                    <div className="mb-2">
                        <label className="form-label alice-form-label" htmlFor="app-name-field">
                            {t("form_labels.app_name")}
                            <span style={{ color: '#FF6B6B' }}>*</span>
                            {isProjectNameUsed && (
                                <span className='text-danger'>&nbsp;{t("errors.app_name_taken")}</span>
                            )}
                            {hasSpecialCharacters && (
                                <span className='text-danger'>&nbsp;{t("errors.app_name_has_sp_chars")}</span>
                            )}
                            {form.projectName && startsWithNumber(form.projectName.trim()) && (
                                <span className='text-danger'>&nbsp;{t("errors.app_name_can_not_start_with_number")}</span>
                            )}
                        </label>
                        <input
                            id="app-name-field"
                            type="text"
                            className="form-control"
                            placeholder={t("form_labels.app_name_placeholder")}
                            name="projectName"
                            onChange={handleFormChange}
                            value={form.projectName}
                            maxLength={64}
                            autoFocus
                        />
                    </div>
                    {showLanguageDropdown && (
                        <div className="mb-2">
                            <label className="form-label alice-form-label">
                                {t("form_labels.select_language")}
                            </label>
                            <select
                                name="language"
                                className="form-select form-control"
                                value={form.language}
                                onChange={handleFormChange}
                            >
                                {LanguagesMap.map(lang => (
                                    <option key={lang.code} value={lang.code}>{lang.name}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    {showAIFields && (
                        <div className="mb-3">
                            <label className="form-label alice-form-label">
                                {t("form_labels.description")}
                            </label>
                            <textarea
                                className="form-control"
                                placeholder={t("form_labels.description_placeholder")}
                                name="description"
                                onChange={handleFormChange}
                                value={form.description}
                                rows={5}
                            />
                        </div>
                    )}
                    <div className="alice-form-divider d-flex align-items-center mb-3">
                        <div className="alice-form-divider-line" />
                        <span className="alice-form-divider-text">OR</span>
                        <div className="alice-form-divider-line" />
                    </div>
                    <div className="alice-form-upload-box mb-4">
                        <div className="alice-form-upload-icon">
                            <i className="fa fa-cloud-arrow-up" />
                        </div>
                        <div className="two-bot-upload-title">{workspaceT("upload_requirements_title")}</div>
                        <div className="two-bot-upload-desc">{workspaceT("upload_requirements_desc")}</div>
                        <label htmlFor="project-file-upload" className="btn btn-light" style={{ border: "1px solid #e5e7eb", fontWeight: 500 }}>
                            <i className="fa fa-upload me-2" />{workspaceT("browse_files")}
                            <input
                                id="project-file-upload"
                                type="file"
                                className="d-none"
                                accept=".pdf,.doc,.docx,.txt"
                                onChange={handleFileChange}
                            />
                        </label>
                        {fileName && (
                            <div className="alice-form-upload-file">{t("upload.selected", { fileName })}</div>
                        )}
                        {fileError && (
                            <div className="text-danger" style={{ marginTop: 4 }}>{fileError}</div>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}

export default AliceProjectDetailsForm;